import AWS from 'aws-sdk';
import Environment from 'src/types/Environment';
import { getLogins } from 'src/helpers/AuthenticatedIdentity';
import Currency from 'src/types/Currency';
import { CheckoutSessionDetails, ChargePermission, BuyerDetail } from 'src/types/WebCheckoutTypes';

const apigClientFactory = require('aws-api-gateway-client').default;

// as defined in docs below, plus HTTP status code as additional field
// https://developer.amazon.com/docs/amazon-pay-api-v2/error-handling.html
export interface IApiError {
    httpStatusCode: number;
    reasonCode: string;
    message: string;
}

export default class ApiGatewayAdapter {
    protected baseUrl: string = Environment.apiGatewayUrl;

    protected region: string = '';

    protected cognitoPoolId: string = Environment.cognitoPoolId;

    protected requestHeaders = {
        'Content-Type': 'application/json',
    };

    protected method: string = 'GET';

    public apiGatewayClient;

    public createAPIClientHelper(data) {
        this.apiGatewayClient = apigClientFactory.newClient({
            invokeUrl: this.baseUrl,
            accessKey: data?.Credentials?.AccessKeyId,
            secretKey: data?.Credentials?.SecretKey,
            sessionToken: data?.Credentials?.SessionToken,
            region: 'us-east-1',
        });
    }

    public async createApiGatewayClient() {
        const self = this;

        if (!self.isCredentialExpired()) {
            if (self.apiGatewayClient != null) {
                // if the client already exists and credentials have not expired
                return true;
            }
            self.createAPIClientHelper(self.getCredentials().data);
            return true;
        }

        AWS.config.region = 'us-east-1';
        const cognitoidentity = new AWS.CognitoIdentity();

        // A Logins map promotes this identity to the *authenticated* role, which is
        // what moves execute-api:Invoke off the guest role. Resolves to undefined on
        // any stage without a user pool configured, leaving the guest flow unchanged.
        const logins = await getLogins();
        const loginsParam = logins ? { Logins: logins } : {};

        const identity = await cognitoidentity
            .getId({ IdentityPoolId: self.cognitoPoolId, ...loginsParam })
            .promise();

        const credentials = await cognitoidentity
            .getCredentialsForIdentity({ IdentityId: identity.IdentityId!, ...loginsParam })
            .promise();

        self.storeCredentials(credentials);
        self.createAPIClientHelper(credentials);
        return true;
    }

    public storeCredentials(data) {
        window.sessionStorage.setItem('cognito-credentials', JSON.stringify({ expirationTime: Date.now() + 5 * 60 * 1000, data }));
    }

    public getCredentials() {
        return JSON.parse(window.sessionStorage.getItem('cognito-credentials') || '{}');
    }

    isCredentialExpired() {
        const credentials = this.getCredentials();
        const expirationTime = credentials?.expirationTime;

        return expirationTime < Date.now() || expirationTime === undefined;
    }

    public setRegion(region: string) {
        this.region = (region !== undefined) ? region : '';
    }

    public async getCheckoutSession(checkoutSessionId: string): Promise<CheckoutSessionDetails> {
        await this.createApiGatewayClient();

        return new Promise((resolve, reject) => {
            const params = {
                headers: this.requestHeaders,
                queryParams: {
                    region: this.region,
                    checkoutSessionId,
                },
            };
            this.apiGatewayClient.invokeApi({}, '/getCheckoutSession', this.method, params)
                .then((response) => {
                    const result = JSON.parse(response.data as string) as CheckoutSessionDetails;
                    resolve(result);
                    console.log('GetCheckoutSession Response:\n');
                    console.dir(result);
                }, (err) => {
                    console.log(err);
                    reject(err);
                });
        });
    }

    public async updateCheckoutSession(checkoutSessionId: string, currency: Currency, returnUrl: string): Promise<CheckoutSessionDetails> {
        const storedFinalTotal: string = localStorage.getItem('finalTotal') || '0.00';
        const finalTotal = currency.code === 'JPY' ? Number(Number(storedFinalTotal) * currency.factor).toFixed(0) : Number(storedFinalTotal) * currency.factor;
        await this.createApiGatewayClient();
        return new Promise((resolve, reject) => {
            const params = {
                headers: this.requestHeaders,
                queryParams: {
                    region: this.region,
                    checkoutSessionId,
                    currencyCode: currency.code,
                    amount: finalTotal,
                    returnUrl,
                },
            };
            this.apiGatewayClient.invokeApi({}, '/updateCheckoutSession', this.method, params)
                .then((response) => {
                    const result = JSON.parse(response.data as string) as CheckoutSessionDetails;
                    resolve(result);
                    console.log('UpdateCheckoutSession Response:\n');
                    console.dir(result);
                }, (err) => {
                    console.log(err);
                    reject(err);
                });
        });
    }

    public async completeCheckoutSession(checkoutSessionId: string, currency: Currency): Promise<CheckoutSessionDetails> {
        const storedFinalTotal: string = localStorage.getItem('finalTotal') || '0.00';
        const finalTotal = currency.code === 'JPY' ? Number(Number(storedFinalTotal) * currency.factor).toFixed(0) : Number(storedFinalTotal) * currency.factor;
        const url = `${this.baseUrl}/completeCheckoutSession?region=${this.region}&checkoutSessionId=${checkoutSessionId}&amount=${finalTotal}&currencyCode=${currency.code}`;
        await this.createApiGatewayClient();
        return new Promise((resolve, reject) => {
            const params = {
                headers: this.requestHeaders,
                queryParams: {
                    region: this.region,
                    checkoutSessionId,
                    amount: finalTotal,
                    currencyCode: currency.code,

                },
            };
            this.apiGatewayClient.invokeApi({}, '/completeCheckoutSession', this.method, params)
                .then((response) => {
                    const result = JSON.parse(response.data as string) as CheckoutSessionDetails;
                    resolve(result);
                    console.log('CompleteCheckoutSession Response:\n');
                    console.dir(result);
                }, (err) => {
                    // let error = (err.response) ? err.response : err;
                    const result = JSON.parse(err.response.data as string) as IApiError;
                    result.httpStatusCode = err.response.status;
                    reject(result);
                });
        });
    }

    public async getChargePermission(chargePermissionId: string): Promise<ChargePermission> {
        const url = `${this.baseUrl}/getChargePermission?region=${this.region}&chargePermissionId=${chargePermissionId}`;
        await this.createApiGatewayClient();
        return new Promise((resolve, reject) => {
            const params = {
                headers: this.requestHeaders,
                queryParams: {
                    region: this.region,
                    chargePermissionId,
                },
            };
            this.apiGatewayClient.invokeApi({}, '/getChargePermission', this.method, params)
                .then((response) => {
                    const result = JSON.parse(response.data as string) as ChargePermission;
                    resolve(result);
                    console.log('getChargePermission Response:\n');
                    console.dir(result);
                }, (err) => {
                    console.log(err);
                    reject(err);
                });
        });
    }

    public async getBuyer(buyerToken: string): Promise<BuyerDetail> {
        const url = `${this.baseUrl}/getBuyer?region=${this.region}&buyerToken=${buyerToken}`;
        await this.createApiGatewayClient();
        return new Promise((resolve, reject) => {
            const params = {
                headers: this.requestHeaders,
                queryParams: {
                    region: this.region,
                    buyerToken,
                },
            };
            this.apiGatewayClient.invokeApi({}, '/getBuyer', this.method, params)
                .then((response) => {
                    const result = JSON.parse(response.data as string) as BuyerDetail;
                    resolve(result);
                }, (err) => {
                    console.log(err);
                    reject(err);
                });
        });
    }

    public async getApplicationConfig(url): Promise<any> {
        const queryParams = new URLSearchParams(new URL(url).search);
        const region = queryParams.get('region');

        await this.createApiGatewayClient();
        return new Promise((resolve, reject) => {
            const params = {
                headers: this.requestHeaders,
                queryParams: {
                    region,
                },
            };
            this.apiGatewayClient.invokeApi({}, '/getApplicationConfig', this.method, params)
                .then((response) => {
                    const result = response;
                    resolve(result);
                }, (err) => {
                    console.log(err);
                    reject(err);
                });
        });
    }

    public async generateButtonSignature(url): Promise<any> {
        const queryObject = {};
        const queryObjectParams = new URLSearchParams(new URL(url).search);

        // iterate over all keys to create query
        for (const key of queryObjectParams.keys()) {
            queryObject[key] = queryObjectParams.get(key);
        }

        await this.createApiGatewayClient();
        return new Promise((resolve, reject) => {
            const params = {
                headers: this.requestHeaders,
                queryParams: queryObject,
            };
            this.apiGatewayClient.invokeApi({}, '/generateButtonSignature', this.method, params)
                .then((response) => {
                    const result = JSON.stringify(response.data) as string;
                    resolve(result);
                }, (err) => {
                    console.log(err);
                    reject(err);
                });
        });
    }
}
